import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to manage resend timer with countdown for email-related operations
 *
 * @param cooldownSeconds - Number of seconds to wait before allowing resend (default: 30)
 * @returns Object containing timer state and control functions
 *
 * @example
 * ```tsx
 * function PasswordResetPage() {
 *   const { canResend, secondsRemaining, startTimer, reset } = useResendTimer(30);
 *
 *   const handleResend = async () => {
 *     await sendEmail();
 *     startTimer(); // Start 30-second countdown
 *   };
 *
 *   return (
 *     <button disabled={!canResend} onClick={handleResend}>
 *       {canResend ? 'Resend' : `Wait ${secondsRemaining}s`}
 *     </button>
 *   );
 * }
 * ```
 */
export function useResendTimer(cooldownSeconds: number = 30) {
  const [canResend, setCanResend] = useState(true);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    },
    [],
  );

  /**
   * Start the countdown timer
   */
  const startTimer = useCallback(() => {
    setCanResend(false);
    setSecondsRemaining(cooldownSeconds);

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Start countdown
    timerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          // Timer finished
          setCanResend(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [cooldownSeconds]);

  /**
   * Reset the timer to initial state
   */
  const reset = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCanResend(true);
    setSecondsRemaining(0);
  }, []);

  return {
    canResend,
    secondsRemaining,
    startTimer,
    reset,
  };
}
